/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-console */
/* eslint-disable react/no-array-index-key */
import React, { FunctionComponent, useMemo, useRef, useState } from "react";
import { InteractiveElement, StyledIconButton, iconButtonSize } from "@iventis/styles";
import { Theme } from "@emotion/react";
import { Checkbox, Radio, useTheme } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { dataTestIds } from "@iventis/testing";
import { SimpleTooltip, DisabledOverlay, CursorTooltip } from "@iventis/components";
import { useTreeBrowser } from "../../../state/tree-browser-hooks";
import { StyledLibraryListNode, StyledExpansionContainer, getIndentPx, NewStyledEmptyNodeBordered } from "../styles/library-list-node.styles";
import { StyledNameContainer, StyledNodeName, StyledEmptyNodeMessage } from "../styles/library-node.styles";
import { NewStyledCheckBoxContainer, MARGIN_WIDTH_PX } from "../styles/library-shared-node.styles";
import { LibraryOverflowPopover } from "./library-overflow-popover";
import { LibraryNodeProps } from "../types/library-node.types";
import { TreeBrowserDomainNodeContext } from "../helpers/library.helpers";

export const LibraryListNodeComponent: FunctionComponent<LibraryNodeProps> = ({
    content,
    node,
    preview: icon,
    expandable,
    open,
    text,
    overflowOptions,
    disableActions,
    disabled = false,
    selectable = { disabled: false, tooltip: undefined },
    draggingNode,
    nodeText = node.name,
    isDragging = false,
    subtitle,
    isNameClickable,
}) => {
    const theme = useTheme<Theme>();

    const [{ expandedNodeIds, selectedNodeIds, loadingNodeIds, singleSelectionOnly }, treeBrowserContext] = useTreeBrowser(
        TreeBrowserDomainNodeContext,
        node.id,
        "expandedNodeIds",
        "selectedNodeIds",
        "loadingNodeIds",
        "singleSelectionOnly"
    );

    const loading = loadingNodeIds.includes(node.id);
    const selected = draggingNode ? false : selectedNodeIds.includes(node.id);
    const expanded = expandedNodeIds.includes(node.id);
    const overflowButtonRef = useRef<HTMLDivElement>();
    const [overflowOpen, setOverflowOpen] = useState<boolean>(false);

    /** Memoise partitions for efficiency */

    // Only update the checkbox if selected, or disable selected is changed
    const checkBox: React.ReactNode = useMemo(
        () =>
            !disableActions ? (
                <SimpleTooltip text={selectable.tooltip} placement="top-start" disabled={!selectable.disabled}>
                    <NewStyledCheckBoxContainer onDoubleClick={(e) => e.stopPropagation()}>
                        {!draggingNode && !selectable.disabled && (
                            <>
                                {singleSelectionOnly ? (
                                    // MUI, in it's benevolence, did not put a classname on the radio button icon, so we have to use our own
                                    <Radio
                                        className={`${selectable.disabled ? "selection-disabled" : "colour-on-focus-light"}`}
                                        size="small"
                                        checked={selected}
                                        data-testid={`select-node-${node.name}-radio`}
                                        disabled={selectable.disabled}
                                        icon={
                                            <span style={{ display: "flex", justifyContent: "center" }}>
                                                <FontAwesomeIcon icon={["far", "circle"]} />
                                            </span>
                                        }
                                        checkedIcon={
                                            <span style={{ display: "flex", justifyContent: "center" }}>
                                                <FontAwesomeIcon icon={["far", "circle-dot"]} />
                                            </span>
                                        }
                                        color="default"
                                    />
                                ) : (
                                    <Checkbox
                                        className={`${selectable.disabled ? "selection-disabled" : "colour-on-focus-light"}`}
                                        data-cy={dataTestIds.libraryNode.checkbox(node.id)}
                                        size="small"
                                        checked={selected}
                                        disabled={selectable.disabled}
                                        color="default"
                                    />
                                )}
                                {selectable.disabled && <DisabledOverlay />}
                            </>
                        )}
                    </NewStyledCheckBoxContainer>
                </SimpleTooltip>
            ) : (
                <div style={{ width: iconButtonSize, height: iconButtonSize }} />
            ),
        [selected, selectable.disabled, disableActions]
    );

    const nodeNameContainerRef = useRef<HTMLButtonElement>();

    const textInOverflow = useMemo(() => nodeNameContainerRef.current?.offsetWidth < nodeNameContainerRef.current?.scrollWidth, [node, nodeNameContainerRef.current]);

    // Only update the name if relevant information changes
    const name: React.ReactNode = useMemo(
        () => (
            <StyledNameContainer>
                {disabled && <DisabledOverlay />}
                <StyledExpansionContainer indentLevel={node.indentLevel}>
                    {expandable && (
                        <InteractiveElement
                            data-cy={`expand-node-id-${node.id}`}
                            data-testid={`expand-node-${node.name}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                treeBrowserContext.toggleExpandNode(node);
                            }}
                        >
                            {!draggingNode && <FontAwesomeIcon icon={loading ? "circle-notch" : expanded ? "caret-down" : "caret-right"} size="lg" spin={loading} />}
                        </InteractiveElement>
                    )}
                </StyledExpansionContainer>
                <span className="icon">{icon}</span>
                <div style={{ display: "flex", flexDirection: "column", maxWidth: "calc(100% - 74px)" }}>
                    <CursorTooltip text={nodeText} disabled={isDragging || !textInOverflow}>
                        <StyledNodeName
                            ref={nodeNameContainerRef}
                            selected={selected}
                            onClick={(event) => {
                                if (!open || !isNameClickable) {
                                    return;
                                }
                                event.stopPropagation();
                                open();
                            }}
                            isNameClickable={isNameClickable}
                        >
                            {nodeText}
                        </StyledNodeName>
                    </CursorTooltip>
                    {subtitle}
                </div>
            </StyledNameContainer>
        ),
        [node.indentLevel, selected, nodeText, open, textInOverflow, isNameClickable, icon, disabled]
    );

    // Only update the overflow if overflow related things change
    const overflow: React.ReactNode = useMemo(
        () => (
            <>
                <div className="content">{content}</div>

                {overflowOptions?.length ? (
                    <>
                        <StyledIconButton
                            className="colour-on-focus"
                            onClick={(event) => {
                                event.stopPropagation();
                                setOverflowOpen(true);
                            }}
                            data-testId={`node-overflow-modal-button-${node.name}`}
                        >
                            <span ref={overflowButtonRef}>
                                <FontAwesomeIcon size="xs" icon="ellipsis" />
                            </span>
                        </StyledIconButton>
                        <LibraryOverflowPopover
                            overflowButtonRef={overflowButtonRef}
                            overflowOptions={overflowOptions}
                            overflowOpen={overflowOpen}
                            setOverflowOpen={setOverflowOpen}
                            node={node}
                        />
                    </>
                ) : (
                    <StyledIconButton disabled />
                )}
            </>
        ),
        [content, overflowOptions, overflowOpen, setOverflowOpen, overflowButtonRef?.current]
    );

    return (
        <>
            <StyledLibraryListNode
                singleTopRowButton={false}
                fontWeight={400}
                borderColour={theme.shades.three}
                selected={selected}
                isFavourite={node.favourite}
                emptyNode={false}
                onClick={(event) => {
                    if (selectable.disabled) {
                        return;
                    }
                    treeBrowserContext.toggleSelectNodes([node]);
                    // Stops the event going further in the DOM which made the node to be toggled twice
                    event.preventDefault();
                }}
                onDoubleClick={() => isNameClickable && open()}
                selectionDisabled={selectable.disabled}
                draggingNode={draggingNode}
                data-testid={`node-${node.name}`}
            >
                <>
                    {!draggingNode && checkBox}

                    <div className="bordered-container">
                        <span>{name}</span>
                        {!draggingNode && overflow}
                    </div>
                </>
            </StyledLibraryListNode>

            {expanded && !loading && !(node.childCount > 0) && (
                <NewStyledEmptyNodeBordered marginLeft={getIndentPx(node.indentLevel) + MARGIN_WIDTH_PX}>
                    <StyledEmptyNodeMessage>{text.emptyMessage}</StyledEmptyNodeMessage>
                </NewStyledEmptyNodeBordered>
            )}
        </>
    );
};
